import { useState, useRef } from 'react';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { getCardErrorMessage } from '../utils/stripeErrors';

interface CardPaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
  sellingPrice?: number;
  companyName?: string;
  /** Couleur du bouton de validation (garant) ; couleurs Affinicar par défaut. */
  accentColor?: string;
  accentTextColor?: string;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#333333',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#df1b41',
    },
  },
};

/**
 * Formulaire carte bancaire pour PaymentIntent (paiement en ligne unique).
 * Utilise confirmCardPayment avec support 3D Secure.
 */
export function CardPaymentForm({
  clientSecret,
  onSuccess,
  sellingPrice,
  companyName,
  accentColor,
  accentTextColor,
}: CardPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const clickedOnceRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || clickedOnceRef.current || !stripe || !elements) return;

    clickedOnceRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        setError('Veuillez renseigner un numéro de carte valide.');
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: { name: cardholderName },
        },
      });

      if (confirmError) {
        // Vérifier le statut réel du PaymentIntent
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        if (paymentIntent?.status === 'succeeded') {
          onSuccess();
          return;
        }
        setError(getCardErrorMessage(confirmError));
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      setError(getCardErrorMessage(err));
      clickedOnceRef.current = false;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tw-space-y-4">
      {/* Cardholder name */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          Nom du titulaire
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Jean Dupont"
          required
          disabled={loading}
          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm disabled:tw-opacity-50"
        />
      </div>

      {/* Card number */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          Numéro de carte
        </label>
        <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-3">
          <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {/* Expiry & CVC */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Date d'expiration
          </label>
          <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-3">
            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            CVC
          </label>
          <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-3">
            <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-flex tw-items-start tw-gap-2">
          <AlertTriangle className="tw-w-4 tw-h-4 tw-text-red-500 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-text-red-700 tw-mb-0">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!stripe || loading}
        style={accentColor ? { backgroundColor: accentColor, color: accentTextColor ?? '#ffffff' } : undefined}
        className="tw-w-full tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-px-4 tw-py-3 tw-font-medium tw-text-sm hover:tw-bg-affinicar-secondary hover:tw-brightness-95 tw-transition tw-shadow-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            Traitement en cours...
          </>
        ) : (
          'Payer maintenant'
        )}
      </button>

      {/* Payment info text */}
      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
          {sellingPrice && companyName ? (
            <>
              En cliquant sur &laquo;&nbsp;Payer maintenant&nbsp;&raquo;, vous autorisez le débit
              de <strong>{sellingPrice.toFixed(2)}&euro;</strong> sur votre carte bancaire
              au profit de {companyName}. La transaction est sécurisée et chiffrée.
            </>
          ) : (
            <>
              En cliquant sur &laquo;&nbsp;Payer maintenant&nbsp;&raquo;, vous autorisez le débit
              du montant indiqué sur votre carte bancaire. La transaction est sécurisée et chiffrée.
            </>
          )}
        </p>
      </div>

      {/* Security badge */}
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-gray-400">
        <Lock className="tw-w-3 tw-h-3" />
        <span className="tw-text-xs">Paiement sécurisé via Stripe</span>
      </div>
    </form>
  );
}
